import { Dropdown } from "antd"
import { NotificationInstance } from "antd/es/notification/interface"
import {
  Check,
  CornerDownRight,
  FileSpreadsheet,
  Loader,
  Paperclip,
  Plus,
  SendHorizonal,
  Telescope,
  X
} from "lucide-react"
import {
  forwardRef,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"
import { useTranslation } from "react-i18next"

import ButtonPrimary from "~/components/base-button"
import CustomTooltip from "~/components/custom-tooltip"
import SelectFilesPicker from "~/components/select-files-picker"
import { normalizeFileId } from "~/components/select-files-picker/types"
import { useAgentFileUpload } from "~/hooks/useAgentFileUpload"
import useGlobalStore from "~/store/global"

import FileChip from "./file-chip"

import type { AgentFile } from "~/hooks/useAgentFileUpload"

const DEFAULT_EXTENSIONS = [".pdf", ".docx", ".pptx", ".xlsx"]

const handleInputResize = (ref: RefObject<HTMLTextAreaElement>) => {
  const textarea = ref.current
  if (!textarea) return
  textarea.style.height = "auto"
  textarea.style.height = `${textarea.scrollHeight}px`
}

export interface AgentChatInputProps {
  placeholder: string
  isLoading: boolean
  inputRef?: RefObject<HTMLTextAreaElement>
  notificationApi: NotificationInstance
  onSend: (
    message: string,
    context: {
      attachedFiles: {
        name: string
        fileId: string
        source?: string
        isTemplate?: boolean
        s3Path?: string
      }[]
      researchEnabled: boolean
      isTemplateFill?: boolean
    }
  ) => Promise<void>

  sessionId: string
  ensureSessionId?: () => Promise<string>
  parentSessionId?: string
  showMyFilesInRfpResponse?: boolean

  maxFiles?: number
  maxFileSizeMB?: number
  acceptedExtensions?: string[]
  /** File names already attached in prior chat messages — used for dedup. */
  historyFileNames?: string[]

  showDeepResearch?: boolean
  deepResearchDefaultOn?: boolean
  deepResearchTooltip?: string

  quotedText?: string | null
  onClearQuote?: () => void

  leftControls?: ReactNode

  onFilesStateChange?: (files: AgentFile[]) => void

  /**
   * When true, the My Files picker auto-opens in "template" mode once on
   * mount — equivalent to the user clicking "+ → Fill template". Used by the
   * agentOS "Fill Templates" workflow entry so the drawer lands straight on
   * the template picker. One-shot (guarded by a ref) so it doesn't re-open
   * after the user closes it.
   */
  autoOpenTemplatePicker?: boolean
}

export interface AgentChatInputHandle {
  addFiles: (files: File[]) => Promise<void>
  clear: () => void
  focus: () => void
}

const AgentChatInputInner = (
  {
    placeholder,
    isLoading,
    inputRef: externalInputRef,
    notificationApi,
    onSend,
    sessionId,
    ensureSessionId,
    parentSessionId,
    showMyFilesInRfpResponse,
    maxFiles = 5,
    maxFileSizeMB = 10,
    acceptedExtensions = DEFAULT_EXTENSIONS,
    historyFileNames,
    showDeepResearch = false,
    deepResearchDefaultOn = false,
    deepResearchTooltip,
    quotedText,
    onClearQuote,
    leftControls,
    onFilesStateChange,
    autoOpenTemplatePicker = false
  }: AgentChatInputProps,
  ref: React.ForwardedRef<AgentChatInputHandle>
) => {
  const { t } = useTranslation()

  const internalInputRef = useRef<HTMLTextAreaElement>(null)
  const inputRef = externalInputRef ?? internalInputRef
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [canSubmit, setCanSubmit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [researchEnabled, setResearchEnabled] = useState(deepResearchDefaultOn)
  const [pickerOpen, setPickerOpen] = useState(false)
  // Which `+` menu action opened the picker: "upload" = normal attachment,
  // "template" = pick ONE file to fill. Drives the picker's title/CTA/single-
  // select/TEMPLATE-tag, and how onSelect treats the pick.
  const [pickerMode, setPickerMode] = useState<"upload" | "template">("upload")
  // The attached file that is the fill TEMPLATE (replaces the old
  // `× Fill template` pill). Stored as its normalized file_id (picker) or
  // localId (native upload fallback). When set, the send carries
  // is_template_fill=true and the chip renders the TEMPLATE tag.
  const [templateFileId, setTemplateFileId] = useState<string | null>(null)
  // Non-picker tenants: "Fill template" opens the native input; the next
  // completed fillable upload becomes the template (effect below).
  const pendingTemplateRef = useRef(false)

  const {
    files,
    completedFiles,
    allDone,
    isInitializing,
    addFiles,
    mergeCompletedFiles,
    attachExistingFiles,
    removeFile,
    retryFile,
    clear
  } = useAgentFileUpload({
    sessionId,
    ensureSessionId,
    parentSessionId,
    showMyFilesInRfpResponse,
    maxFiles,
    maxFileSizeMB,
    acceptedExtensions,
    notificationApi,
    historyFileNames
  })

  useEffect(() => {
    onFilesStateChange?.(files)
  }, [files, onFilesStateChange])

  // Template filler is released behind a flag (default off). Gates the
  // auto-open-picker effect below and the "Fill template" `+`-menu entry.
  const enableTemplateAgentV2 = useGlobalStore(
    (s) => s.user?.feature_flags?.enable_template_agent_v2
  )

  // One-shot: auto-open the template picker on mount when the caller asked
  // for it (agentOS "Fill Templates" entry). Mirrors the "+ → Fill template"
  // menu action (pickerMode="template" + open). Guarded so it fires once and
  // doesn't re-open after the user dismisses the picker.
  const autoOpenedTemplateRef = useRef(false)
  useEffect(() => {
    if (
      enableTemplateAgentV2 &&
      autoOpenTemplatePicker &&
      !autoOpenedTemplateRef.current
    ) {
      autoOpenedTemplateRef.current = true
      setPickerMode("template")
      setPickerOpen(true)
    }
  }, [autoOpenTemplatePicker, enableTemplateAgentV2])

  useImperativeHandle(
    ref,
    () => ({
      addFiles,
      clear,
      focus: () => inputRef.current?.focus()
    }),
    [addFiles, clear, inputRef]
  )

  // ENG-1233: require a non-empty query before send is enabled, even when
  // files are attached. Attaching files alone is no longer enough — users
  // need to actually ask something about the file.
  const shouldEnableSend = useCallback(
    (text?: string) => {
      const value = text ?? inputRef.current?.value ?? ""
      return value.trim() !== ""
    },
    [inputRef]
  )

  useEffect(() => {
    setCanSubmit(shouldEnableSend())
  }, [completedFiles.length, shouldEnableSend])

  const acceptAttr = acceptedExtensions.join(",")

  // ENG-1249 / ENG-1298 features (picker, attachExistingFiles, UX polish)
  // are gated behind `show_my_files_in_rfp_response`. When OFF, fall back
  // to the legacy native file picker → addFiles upload-only path. Honour
  // either the explicit prop OR the user/tenant feature flag so Ask-OS
  // consumers that don't pass the prop still get gated.
  const tenantFlag = useGlobalStore(
    (s) => s.user?.feature_flags?.show_my_files_in_rfp_response
  )
  const userFlag = useGlobalStore(
    (s) => s.featureFlags?.show_my_files_in_rfp_response
  )
  const enableMyFilesPicker =
    showMyFilesInRfpResponse ?? (!!tenantFlag || !!userFlag)

  const onAttachClick = () => {
    if (enableMyFilesPicker) {
      setPickerOpen(true)
    } else {
      fileInputRef.current?.click()
    }
  }

  // "Fill template": pick ONE file to fill. Uses the My Files picker in
  // template mode when available; otherwise falls back to the native input
  // and marks the next completed upload as the template (effect above).
  const onFillTemplateClick = () => {
    setPickerMode("template")
    if (enableMyFilesPicker) {
      setPickerOpen(true)
    } else {
      pendingTemplateRef.current = true
      fileInputRef.current?.click()
    }
  }

  const onFilesPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || [])
    if (fileInputRef.current) fileInputRef.current.value = ""
    if (!picked.length) return
    await addFiles(picked)
  }

  const hasPendingMedia = files.some(
    (f) => f.transcription_status === "pending"
  )
  // Defined here (above the `+` menu memo) so the menu's "Upload"
  // disabled flag can reference it. Mirrors the original computation
  // below — kept in sync via single source of truth.
  const disableAttach = isLoading || isSubmitting || files.length >= maxFiles

  // Identify the attached file that is the fill TEMPLATE — by picker
  // file_id, or (native-upload fallback) by localId.
  const isTemplateFile = useCallback(
    (f: AgentFile) => {
      if (!templateFileId) return false
      const id = normalizeFileId(f.fileId || f.pickFileId || "")
      return (!!id && id === templateFileId) || f.localId === templateFileId
    },
    [templateFileId]
  )

  // Removing the template file also clears the template intent (so the
  // `+` menu re-enables "Fill template" and the send drops is_template_fill).
  const handleRemoveFile = useCallback(
    (localId: string) => {
      const f = files.find((x) => x.localId === localId)
      if (f && isTemplateFile(f)) setTemplateFileId(null)
      removeFile(localId)
    },
    [files, isTemplateFile, removeFile]
  )

  // Non-picker fallback: once the user's chosen template upload completes,
  // mark it as the template.
  useEffect(() => {
    if (!pendingTemplateRef.current || templateFileId) return
    const f = completedFiles.find((c) => {
      const n = (c.name || "").toLowerCase()
      return n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".docx")
    })
    if (f) {
      setTemplateFileId(
        normalizeFileId(f.fileId || f.pickFileId || "") || f.localId
      )
      pendingTemplateRef.current = false
    }
  }, [completedFiles, templateFileId])

  // `+` menu items in antd's Dropdown shape. Matches the header
  // ProfileDropdown's styling: `flex items-center gap-2 text-13 w-full`
  // wrapper with a `dark:text-dark-6 text-dark-2 w-4 h-4` icon. Antd's
  // Dropdown handles positioning, click-outside, and portaling for us
  // — no manual anchor / overlay machinery needed.
  const plusMenuItems = useMemo(
    () => [
      {
        key: "upload",
        onClick: () => {
          setPickerMode("upload")
          onAttachClick()
        },
        disabled: disableAttach,
        label: (
          <div className="flex items-center gap-2 text-13 w-full">
            <Paperclip className="dark:text-dark-6 text-dark-2 w-4 h-4 text-lg" />
            {t("ask_os.label_plus_menu_upload")}
          </div>
        )
      },
      // "Fill template" entry is gated behind enable_template_agent_v2.
      ...(enableTemplateAgentV2
        ? [
            {
              key: "fill_template",
              onClick: () => onFillTemplateClick(),
              // Once a template is attached, "Fill template" is faded +
              // shows "Added"; remove the template chip to pick another.
              disabled: disableAttach || !!templateFileId,
              label: (
                <CustomTooltip
                  placement="right"
                  title={
                    <p className="text-xs">
                      {t("ask_os.tooltip_fill_template_single")}
                    </p>
                  }
                >
                  <div className="flex items-center gap-2 text-13 w-full">
                    <FileSpreadsheet className="dark:text-dark-6 text-dark-2 w-4 h-4 text-lg" />
                    {t("ask_os.label_plus_menu_fill_template")}
                    {templateFileId ? (
                      <span className="ml-1 text-[11px] font-semibold text-primary dark:text-primary-light uppercase tracking-wide">
                        {t("ask_os.label_added")}
                      </span>
                    ) : (
                      <span className="ml-1 text-[11px] text-red-400">
                        {t("ask_os.label_beta_suffix")}
                      </span>
                    )}
                  </div>
                </CustomTooltip>
              )
            }
          ]
        : [])
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disableAttach, templateFileId, t, enableTemplateAgentV2]
  )

  const handleSend = useCallback(async () => {
    if (isSubmitting) return
    if (!allDone) return
    if (hasPendingMedia) return
    const message = inputRef.current?.value ?? ""
    // ENG-1233: query is mandatory regardless of file attachments.
    if (message.trim() === "") return

    // Template-fill intent = a file was attached via "Fill template" and is
    // still attached. The attached template chip is the single source now
    // (replaces the old pill).
    const isTemplateFill =
      !!templateFileId && completedFiles.some(isTemplateFile)

    setIsSubmitting(true)
    const restoreMessage = () => {
      if (inputRef.current) {
        inputRef.current.value = message
        handleInputResize(inputRef)
      }
    }

    if (inputRef.current) {
      inputRef.current.value = ""
      inputRef.current.style.height = "auto"
    }

    try {
      await onSend(message, {
        attachedFiles: completedFiles.map((f) => ({
          name: f.name,
          fileId: f.fileId as string,
          source: f.source,
          // Persisted so chat history re-renders this file with the
          // TEMPLATE tag in prior turns (same session / reload).
          isTemplate: isTemplateFile(f),
          s3Path: f.s3Path
        })),
        researchEnabled,
        isTemplateFill
      })
      clear()
      setResearchEnabled(deepResearchDefaultOn)
      setTemplateFileId(null)
      setCanSubmit(false)
    } catch {
      restoreMessage()
      setCanSubmit(true)
    } finally {
      setIsSubmitting(false)
    }
  }, [
    allDone,
    clear,
    completedFiles,
    deepResearchDefaultOn,
    hasPendingMedia,
    inputRef,
    isSubmitting,
    onSend,
    researchEnabled,
    templateFileId,
    isTemplateFile
  ])

  const uploading = files.some((f) => f.phase === "UPLOADING")
  const blockedWhileProcessing = !allDone
  // The template file (when present) is itself an attachment, so it's
  // covered by blockedWhileProcessing/allDone — no separate gate needed.
  const sendDisabled =
    isLoading ||
    isSubmitting ||
    !canSubmit ||
    blockedWhileProcessing ||
    hasPendingMedia

  return (
    <div className="w-full rounded-lg p-[3px] bg-[linear-gradient(88.01deg,rgba(108,92,231,0.6)_0%,rgba(98,228,254,0.6)_54.69%,rgba(200,194,249,0.6)_98.92%)] shadow-[-2px_3px_10.9px_2px_#6C5CE74F] overflow-hidden">
      <div className="rounded-lg p-2 border-[0.5px] border-light-border dark:border-border bg-genesis dark:bg-dark-2">
        {quotedText && (
          <div
            data-agent-quote-ui="true"
            className="flex items-start gap-2 px-2 py-1.5 mb-2 rounded-lg bg-primary/5 border-l-2 border-primary text-left"
          >
            <CornerDownRight
              size={12}
              className="text-primary mt-0.5 flex-shrink-0"
            />
            <span className="text-xs text-dark-2 dark:text-dark-6 line-clamp-2 flex-1 italic">
              &ldquo;{quotedText}&rdquo;
            </span>
            <button
              type="button"
              className="p-0.5 hover:bg-primary/10 rounded-full text-dark-2 dark:text-dark-6 flex-shrink-0"
              onClick={onClearQuote}
            >
              <X size={12} />
            </button>
          </div>
        )}

        <textarea
          ref={inputRef}
          data-testid="ask-orbitshift-composer-input"
          placeholder={
            templateFileId
              ? t("ask_os.label_template_instructions")
              : placeholder
          }
          className="!resize-none overflow-auto max-h-28 w-full outline-none text-13 text-dark-2 dark:text-dark-6 bg-transparent"
          onInput={() => handleInputResize(inputRef)}
          onChange={(e) => setCanSubmit(shouldEnableSend(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSend()
            }
          }}
          disabled={isLoading || isSubmitting}
        />

        {(files.length > 0 || isInitializing) && (
          <div className="flex flex-wrap gap-2 mb-2 max-h-40 overflow-y-auto pr-1">
            {isInitializing && (
              <span className="inline-flex items-center gap-2 px-2 py-1 text-xs rounded-full border border-light-border dark:border-border bg-white dark:bg-dark-3 text-dark-2 dark:text-dark-6">
                <Loader
                  size={12}
                  className="animate-spin text-primary dark:text-primary-light shrink-0"
                />
                <span>{t("rfp_os.label_initializing_session")}</span>
              </span>
            )}
            {files.map((file) => (
              <FileChip
                key={file.localId}
                file={file}
                disabled={isSubmitting}
                onRemove={handleRemoveFile}
                onRetry={retryFile}
                template={isTemplateFile(file)}
              />
            ))}
          </div>
        )}

        <div className="flex justify-between items-center gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {enableTemplateAgentV2 ? (
              // Flag on: `+` opens a menu with both Upload and Fill template.
              <Dropdown
                menu={{ items: plusMenuItems }}
                placement="bottomLeft"
                trigger={["click"]}
              >
                <button
                  type="button"
                  data-testid="ask-orbitshift-composer-attach-menu"
                  className="px-2 py-1 rounded-full border border-light-border dark:border-border text-dark-2 dark:text-dark-6 disabled:opacity-50 hover:bg-primary/5 dark:hover:bg-primary/10"
                  disabled={isLoading || isSubmitting}
                  aria-haspopup="menu"
                  aria-label={t("rfp_os.label_open_plus_menu", {
                    defaultValue: "Open attach / mode menu"
                  })}
                >
                  {uploading ? (
                    <Loader className="text-primary animate-spin" size={16} />
                  ) : (
                    <Plus size={16} />
                  )}
                </button>
              </Dropdown>
            ) : (
              // Flag off: no Fill template option, so skip the single-item
              // menu and revert to a plain paperclip that opens upload directly.
              <button
                type="button"
                data-testid="ask-orbitshift-composer-attach-plain"
                onClick={() => {
                  setPickerMode("upload")
                  onAttachClick()
                }}
                className="px-2 py-1 rounded-full border border-light-border dark:border-border text-dark-2 dark:text-dark-6 disabled:opacity-50 hover:bg-primary/5 dark:hover:bg-primary/10"
                disabled={isLoading || isSubmitting}
                aria-label={t("ask_os.label_plus_menu_upload")}
              >
                {uploading ? (
                  <Loader className="text-primary animate-spin" size={16} />
                ) : (
                  <Paperclip size={16} />
                )}
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptAttr}
              multiple
              hidden
              onChange={onFilesPicked}
            />

            {/* The old `× Fill template` pill is gone — the chosen template
                now renders as a FileChip (template variant) in the
                attachment row above, with its own remove. */}

            {showDeepResearch && (
              <CustomTooltip
                title={
                  deepResearchTooltip ? (
                    <p className="text-xs">{deepResearchTooltip}</p>
                  ) : null
                }
              >
                <button
                  type="button"
                  className={`text-xs border rounded-full px-2 py-[6px] flex items-center gap-2 ${
                    researchEnabled
                      ? "text-white bg-sky-400 border-sky-400 dark:bg-primary dark:border-primary"
                      : "bg-genesis dark:bg-dark-2 border-light-border dark:border-border text-dark-2 dark:text-dark-6"
                  }`}
                  onClick={() => setResearchEnabled((prev) => !prev)}
                >
                  <Telescope size={16} />
                  {t("rfp_os.label_deep_research", {
                    defaultValue: "Deep research"
                  })}
                  {researchEnabled ? <X size={16} /> : <Plus size={16} />}
                </button>
              </CustomTooltip>
            )}

            {leftControls}
          </div>

          <CustomTooltip
            title={
              blockedWhileProcessing && files.length > 0 ? (
                <p className="text-xs">
                  {t("rfp_os.label_wait_files_processing", {
                    defaultValue: "Waiting for files to finish processing."
                  })}
                </p>
              ) : canSubmit ? null : (
                <p className="text-xs">
                  {t("rfp_os.label_add_your_query", {
                    defaultValue: "Please add your query"
                  })}
                </p>
              )
            }
          >
            <ButtonPrimary
              data-testid="ask-orbitshift-composer-submit"
              className="bg-primary text-white px-4 py-2 h-auto !rounded-full"
              onClick={handleSend}
              disabled={sendDisabled}
            >
              {isLoading || isSubmitting ? (
                <Loader className="animate-spin" size={16} />
              ) : (
                <SendHorizonal size={16} />
              )}
            </ButtonPrimary>
          </CustomTooltip>
        </div>
      </div>

      {enableMyFilesPicker && (
        <SelectFilesPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(picked) => {
            // Split picks by how they need to reach ChatDocQnA. Doc picks
            // (My Files, Shared, AND Knowledge Library) must be attached to
            // the chat session's CHAT_CONTEXT scope so dpp ingests them into
            // the chat OpenSearch indexes ChatDocQnA reads (ENG-1249 /
            // ENG-1298). KB doc picks were previously merged as COMPLETED
            // WITHOUT ingestion, so "summarise this doc" found 0 session
            // docs and returned a blank answer (ENG-2133) — they now route
            // through attachExistingFiles like any other doc; the backend
            // resolves the KB id and ingests a session-scoped copy.
            // Only media stays on the legacy mergeCompletedFiles path:
            // pending-transcription media (transcription completes server-
            // side) and already-transcribed KB media (not chat-doc content).
            const mediaToMerge = picked.filter(
              (p) =>
                (p.is_media && p.transcription_status === "pending") ||
                (p.source === "kb" && p.is_media)
            )
            const docsToAttach = picked.filter(
              (p) =>
                !(
                  (p.is_media && p.transcription_status === "pending") ||
                  (p.source === "kb" && p.is_media)
                )
            )

            if (mediaToMerge.length > 0) {
              const externals: AgentFile[] = mediaToMerge.map((p) => ({
                localId:
                  typeof crypto !== "undefined" &&
                  typeof crypto.randomUUID === "function"
                    ? crypto.randomUUID()
                    : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
                name: p.file_name,
                fileId: p.file_id,
                s3Path: p.s3_path,
                source: p.source,
                phase:
                  p.is_media && p.transcription_status === "pending"
                    ? "PROCESSING"
                    : "COMPLETED",
                uploadPct: 100,
                processingPct:
                  p.is_media && p.transcription_status === "pending" ? 0 : 100,
                transcription_status: p.transcription_status
              }))
              mergeCompletedFiles(externals)
            }

            if (docsToAttach.length > 0) {
              // Fire-and-forget — the hook updates chip phase/status; errors
              // surface through its notification toast.
              void attachExistingFiles(docsToAttach)
            }

            // Template mode is single-select — the one pick becomes the
            // fill template (tracked by its normalized file_id).
            if (pickerMode === "template" && picked[0]) {
              setTemplateFileId(normalizeFileId(picked[0].file_id))
            }

            setPickerOpen(false)
          }}
          availableTabs={["my_files", "kb"]}
          maxFiles={pickerMode === "template" ? 1 : maxFiles}
          title={
            pickerMode === "template"
              ? t("ask_os.label_select_file_to_fill")
              : undefined
          }
          confirmLabel={
            pickerMode === "template" ? (
              // Single inline-flex child — antd Button splits/wraps a
              // fragment (icon + string) onto two lines otherwise.
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <Check size={14} />
                {t("ask_os.label_fill_template_cta")}
              </span>
            ) : undefined
          }
          showTemplateTag={pickerMode === "template"}
          // Template mode: SHOW all files but make only docx/xlsx selectable
          // (others render disabled, not hidden) — and the upload pane is
          // restricted to these. Normal "Upload a file" keeps the full set.
          selectableExtensions={
            pickerMode === "template" ? ["xlsx", "docx"] : undefined
          }
          maxFileSizeMB={maxFileSizeMB}
          accept={acceptAttr}
          currentlyAttached={files
            .map((f) =>
              f.fileId
                ? normalizeFileId(f.fileId)
                : f.pickFileId
                  ? f.pickFileId
                  : null
            )
            .filter((id): id is string => !!id)}
        />
      )}
    </div>
  )
}

const AgentChatInput = forwardRef<AgentChatInputHandle, AgentChatInputProps>(
  AgentChatInputInner
)
AgentChatInput.displayName = "AgentChatInput"

export default AgentChatInput

export { useAgentFileUpload } from "~/hooks/useAgentFileUpload"
export type { AgentFile } from "~/hooks/useAgentFileUpload"
export { default as FileChip } from "./file-chip"
